import { useState } from 'react';
import { Alert, App, Button, Card, Result, Space, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { apiErrorCode, apiErrorMessage } from '@/api/client';
import {
  commitImport,
  downloadTemplate,
  parseImport,
  type ImportCheckResult,
  type ImportCommitResult,
  type ImportRawRow,
} from '@/api/imports';
import { canSubmit, totalsSummary } from '@/domain/imports';
import ImportRowsTable from '@/components/imports/ImportRowsTable';

const { Paragraph, Text } = Typography;

/**
 * 🔴 服务端**明确拒绝**的短码白名单 —— 判据是「服务端一行都没落」且「这不是一次
 * 意外，是服务端已经把请求看完并主动回绝」。命中这个白名单时可以放心提示错误、
 * 不锁上传：用户改完（或者本来就没什么可改，比如空提交）重新走一遍解析→提交
 * 是安全的，因为上一次压根没创建任何东西。
 *
 * - `IMPORT_HAS_ERRORS`      有行没通过校验（commitBatch 在事务外校验阶段判定）
 * - `IMPORT_EMPTY`           提交了空数组
 * - `IMPORT_TOO_MANY_ROWS`   行数超过上限
 * - `BAD_IMPORT_COMMIT`      请求体本身没通过 schema 校验
 * - `IMPORT_TOKEN_CONFLICT`  token 撞了别的组织已用过的同一个随机值（极小概率）
 *
 * 🔴 为什么不能反过来（把「不认识的短码」也当成明确拒绝）：这份名单只能白名单式
 * 增长，不能黑名单式排除。默认必须是「不确定」——一个新短码、一次网络错误、一次
 * 5xx，只要不在这个名单里，就统一按「不确定，锁上传」处理。反过来做的话，任何
 * 我们没预料到的错误都会被当成「已经确认失败」，从而放人重新上传文件，那正是
 * Critical 1 挡的那个入口：重新上传会换一个新的幂等 token，服务端认不出这是
 * 同一批，会把这些收款人和申请再建一遍。
 *
 * 🔴 `IMPORT_UPSTREAM_UNAVAILABLE`（commit 阶段上游超时/不可达，见
 * backend/src/modules/imports/imports.service.ts 的 isAmbiguousUpstreamError）
 * 刻意**不进**这份名单：这一批确实没有落库，但它的安全出路是「用同一个 token 重试」
 * 而不是「重新上传」——放人重新上传比让他多等一次更危险，所以仍然要走下面的
 * Modal + 锁上传这条路径，与其它未知错误一视同仁。
 */
const DEFINITE_REJECTION_CODES = new Set([
  'BAD_INPUT_SOURCE',
  'IMPORT_HAS_ERRORS',
  'IMPORT_EMPTY',
  'IMPORT_TOO_MANY_ROWS',
  'BAD_IMPORT_COMMIT',
  'IMPORT_TOKEN_CONFLICT',
]);

/**
 * 批量导入收款人并同时发起打款申请。admin / finance 可用，member 进不来。
 *
 * 三段在同一页，不做向导跳转：下载 / 上传 → 平铺确认 → 创建。
 *
 * 🔴🔴 **提交成功后整页切成结果态，「立即创建申请」按钮从 DOM 里消失**（不是禁用）。
 * 重复提交会造出一批在打款申请页**看不出是重复**的 PENDING 申请，而财务会把它们
 * 全打出去 —— PRODUCT.md §5.2 的四道闸防的是「同一条申请被打两次」，不防这个。
 * 服务端的幂等 token 是真正的边界，这里只是不给入口。
 */
export default function PayoutBatchImport() {
  const { message, modal } = App.useApp();
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportCheckResult | null>(null);
  const [rawRows, setRawRows] = useState<ImportRawRow[]>([]);
  const [done, setDone] = useState<ImportCommitResult | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  /**
   * 🔴 commit 失败之后的「结果不确定」态。此时绝不能让人重新上传文件——
   * token 是 parse 那一步签发的，重新上传会拿到一个新 token，后端的幂等只认 token
   * 不认内容，会把这一批当成全新的、再建一遍。唯一安全的出口是用同一个
   * `result.token` 再 commit 一次，服务端幂等会回放。所以这里只锁上传入口，
   * 提交按钮保持可点。
   */
  const [commitFailed, setCommitFailed] = useState(false);

  async function onDownload() {
    try {
      await downloadTemplate();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '模板下载失败');
    }
  }

  async function onFile(file: File) {
    setParsing(true);
    setFatal(null);
    setResult(null);
    setRawRows([]); // 上一次解析留下的行不该带到这一次——不能靠“按钮依赖 result”这个别处的条件兜底
    setCommitFailed(false); // 新一轮解析，上一批提交失败的锁定状态不该带过来
    try {
      const r = await parseImport(file);
      setResult(r);
      // 🔴 commit 回传的是**原始单元格**而不是 draft：服务端要从同一份原始输入把校验
      // 重跑一遍。回传 draft 等于让客户端决定最终落库的值
      setRawRows(r.rows.map(({ rowNumber, cells }) => ({ rowNumber, cells })));
    } catch (err) {
      setFatal(apiErrorMessage(err));
    } finally {
      setParsing(false);
    }
  }

  async function onSubmit() {
    if (!result) return;
    setSubmitting(true);
    try {
      const r = await commitImport(result.token, rawRows, result.sourceRecordId);
      setDone(r);
      setCommitFailed(false);
      message.success(r.duplicated ? '这一批之前已经提交过，没有重复创建' : `已创建 ${r.created.length} 条打款申请`);
    } catch (err) {
      // 🔴 服务端**明确拒绝**（一行都没落，且不是意外）时，不锁上传——见
      // DEFINITE_REJECTION_CODES 顶部的注释。这条分支必须窄，宁可漏判成「不确定」，
      // 也不能把一次真的不确定（超时、5xx、未知短码）错判成「已确认失败」。
      const code = apiErrorCode(err);
      if (code !== undefined && DEFINITE_REJECTION_CODES.has(code)) {
        message.error(apiErrorMessage(err));
        return;
      }

      /*
       * 🔴 commit 失败时结果是**未知**的，不是「失败」——请求可能已经在服务端成功处理，
       * 只是响应没能传回来（典型：超时）。这里绝不能只弹一闪而过的 toast：那会诱导人
       * 下意识重新上传同一个文件，而重新上传换来的是一个新的幂等 token，服务端会把它
       * 当成全新一批，造出一整批内容重复、在打款申请页看不出是重复的待打款申请。
       * 用 modal.error 而不是 message.error——同类「结果不确定」场景
       * （PAYOUT_RESULT_UNKNOWN）在 PayoutApplications.tsx 也是这么处理的：
       * 错过一条 toast 太容易，而这条提示必须被看到并读完。
       */
      setCommitFailed(true);
      modal.error({
        title: '这一批的结果还不确定',
        width: 480,
        content: (
          <Space direction="vertical" size={8} style={{ marginTop: 8 }}>
            <Text>{apiErrorMessage(err)}</Text>
            <Text strong>请先去「打款申请」页核对这一批是不是已经创建成功了，再决定下一步。</Text>
            <Text type="danger">
              千万不要重新上传这个文件 —— 重新上传会换成一个新的批次号，系统认不出这是同一批，
              会把这些收款人和申请再建一遍。
            </Text>
            <Text type="secondary">
              确认还没成功的话，直接点下面的「重试（同一批次）」——用的是同一个批次号，
              系统会识别出重复，是安全的。
            </Text>
          </Space>
        ),
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Card title="批量导入">
        <Result
          status="success"
          title={done.duplicated ? '这一批之前已经提交过' : `已创建 ${done.created.length} 条打款申请`}
          subTitle={
            done.duplicated
              ? '系统识别出这是同一批数据，没有重复创建。下面是当初创建出来的记录。'
              : '收款人已作为草稿创建，第一次打款前才会推送给空中云汇。'
          }
          extra={[
            <Link key="apps" to="/payout-applications">
              <Button type="primary">去打款申请</Button>
            </Link>,
            <Link key="payees" to="/payees">
              <Button>去收款人</Button>
            </Link>,
          ]}
        >
          <Paragraph>
            <Text strong>新建的收款人短 id：</Text>
          </Paragraph>
          <Paragraph>
            {done.created.map((c) => (
              <Text code key={c.payeeId} style={{ marginInlineEnd: 8 }}>
                {c.payeeShortId}
              </Text>
            ))}
          </Paragraph>
        </Result>
      </Card>
    );
  }

  return (
    <Card
      title="批量导入收款人并发起申请"
      extra={
        <Space>
          <Button onClick={onDownload}>下载模板</Button>
          {result && (
            <Button
              type="primary"
              loading={submitting}
              // done 恒为 null（走到这里说明上面的早退没触发），但仍原样传给 canSubmit——
              // 不写死成字面量 false，免得以后谁把上面那个 `if (done)` 早退挪走了，
              // 这里悄悄变成一个永远不检查 done 的漏洞。
              disabled={!canSubmit(result, submitting, done !== null)}
              onClick={onSubmit}
            >
              {/* commitFailed 之后文案换成「重试」，让人知道这不是普通的「再点一次」——
                  它安全，是因为走的是同一个 result.token，服务端幂等会回放。 */}
              {commitFailed ? '重试（同一批次）' : '立即创建申请'}
            </Button>
          )}
        </Space>
      }
    >
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        只支持 SWIFT 汇款方式。付款币种就是模板里的「账户币种」——
        系统按收款账户的币种打款，不另选。创建出来的是<Text strong>待打款申请</Text>，
        钱仍然由财务在打款申请页一条条按下去。
      </Paragraph>

      {fatal && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message="这个文件没法用" description={fatal} />
      )}

      <Upload.Dragger
        accept=".xlsx"
        maxCount={1}
        showUploadList={false}
        // 🔴 commit 进行中 / commitFailed 时都要锁住上传入口。
        // 少了 submitting 会让「提交挂起、用户等不及重新拖文件」这条路径绕过锁：
        // 新文件解析完会把 result/rawRows 换成新的，token 也跟着变成新的一个；
        // 随后第一次 commit 才返回失败（其实服务端可能已经成功落库），用户照着
        // Modal 的提示点「重试（同一批次）」时，读到的却是刚换上的新 token ——
        // 服务端的幂等认不出这是同一批，会把这一整批收款人和申请再建一遍。
        disabled={parsing || submitting || commitFailed}
        beforeUpload={(file) => {
          void onFile(file as unknown as File);
          return false; // 自己发请求，不走 antd 的上传
        }}
        style={{ marginBottom: 16 }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {parsing
            ? '正在解析…'
            : commitFailed
              ? '这一批提交结果未知，请先去「打款申请」页核对，不要重新上传'
              : '把填好的模板拖到这里，或点击选择'}
        </p>
        <p className="ant-upload-hint">只接受 .xlsx，单个文件不超过 2 MB</p>
      </Upload.Dragger>

      {result && (
        <>
          <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
            <Alert
              type={result.errorCount > 0 ? 'error' : 'info'}
              showIcon
              message={
                result.errorCount > 0
                  ? `共 ${result.rows.length} 行，其中 ${result.errorCount} 行有问题`
                  : `共 ${result.rows.length} 行，合计 ${totalsSummary(result.totals)}`
              }
              description={
                result.errorCount > 0
                  ? '改好之后重新上传。只要有一行有问题就不能提交 —— 资金链路上「部分成功」事后极难对清楚。'
                  : '请逐行核对收款信息、金额与手续费承担方，确认无误后点右上角「立即创建申请」。'
              }
            />
            {result.errorCount === 0 && result.totals.length > 1 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                合计按币种分别列出，不同币种之间不相加。
              </Text>
            )}
            {result.skippedTemplateRows.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                已跳过模板自带的 {result.skippedTemplateRows.length} 行（第{' '}
                {result.skippedTemplateRows.join('、')} 行——填写要求与示例数据，不计入上面的行数）。
              </Text>
            )}
            {result.noticeCount > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`有 ${result.noticeCount} 处填了这一行用不上的列，已忽略`}
                description="不影响提交，但如果那是你本来想填的内容，说明这一行的国家或币种选错了。"
              />
            )}
            {result.beneficiaryPaysCount > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`其中 ${result.beneficiaryPaysCount} 行由对方承担手续费`}
                description="对方实际到手将少于申请单上的金额。"
              />
            )}
          </Space>

          <ImportRowsTable columns={result.columns} rows={result.rows} />
        </>
      )}
    </Card>
  );
}
